







































































import {
    computed,
    defineComponent,
    onMounted,
    PropType,
    ref,
    watch,
} from '@vue/composition-api';
import CustomConditionsPreview from '@app/promotionHub/shared/components/CustomConditionsPreview.vue';
import PromotionDetails from '@app/promotionHub/promotions/components/PromotionDetails.vue';
import CardActionSection from '@app/promotionHub/shared/components/CardActionSection.vue';
import { Condition } from '@app/promotionHub/shared/types/Condition';
import {
    PromotionBonus,
    PromotionBonusType,
} from '@app/promotionHub/promotions/types/Promotion';
import { BonusKind } from '@core/models/bonus/Bonus';
import {
    useI18n,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import promotionStoreIndex from '@store/modules/ui/user/promotions/promotionIndex';
import authStore from '@store/modules/data/authentication/authenticationIndex';
import modalStore from '@store/modules/ui/shared/modal/modalIndex';
import { minDate } from '@superbet-group/web.lib.utilities';
import { getConfig } from '@superbet-group/web.lib.config';
import BonusProvider from '@core/models/bonus/BonusProvider';
import PromotionWelcomeOfferDetails from './PromotionWelcomeOfferDetails.vue';
import {
    BetlerWelcomeBonus,
    WelcomeBonus,
} from '@core/models/bonus/WelcomeBonus';
import { getBetlerBonusSummary } from '@app/promotionHub/bonuses/utils/bonusHelpers';
import { WelcomeOfferBonusType } from '@core/models/bonus/WelcomeOfferBonusType';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { PromotionHubClickEventLabels } from '@src/app/promotionHub/shared/types/PromotionHubClickEventLabels';
import { useCustomConditionsPreview } from '@app/promotionHub/bonuses/composables/bonuses';
import { getWelcomeOfferModalData } from '../utils/helpers';

export default defineComponent({
    name: 'PromotionActiveCard',
    props: {
        promotionId: {
            type: [Number, String],
            required: true,
        },
        kind: {
            type: String as PropType<BonusKind>,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        image: {
            type: String,
            required: false,
            default: '',
        },
        conditions: {
            type: Array as PropType<Condition[]>,
            required: false,
            default: () => ([]),
        },
        expirationDate: {
            type: Date,
            required: false,
            default: null,
        },
        bonus: {
            type: Object as PropType<PromotionBonus>,
            required: true,
        },
        bonusSummaryTitle: {
            type: String,
            required: false,
            default: '',
        },
        bonusValue: {
            type: String,
            required: false,
            default: '',
        },
        optInCode: {
            type: String,
            required: false,
            default: null,
        },
        welcomeBonuses: {
            type: Array as PropType<WelcomeBonus[]>,
            required: false,
            default: () => ([]),
        },
        isExpanded: {
            type: Boolean,
            required: false,
            default: false,
        },
        isMultiConditionPromotion: {
            type: Boolean,
            required: false,
            default: false,
        },
        termsAndConditionsUrl: {
            type: String,
            required: false,
            default: '',
        },
    },
    // eslint-disable-next-line sonarjs/cognitive-complexity
    setup(props) {
        const { translate } = useI18n();

        const promotionActiveCardDiv = ref<null | HTMLElement>(null);
        const areDetailsVisible = ref(props.isExpanded);
        const isLoading = ref(false);
        const isActionButtonClicked = ref(false);

        const promoModule = useVuexStore<typeof promotionStoreIndex>('ui/user/promotions');
        const promotionOptIn = promoModule.action('promotionOptIn');
        const isPromotionOptInProcessing = promoModule.state('isPromotionOptInProcessing');

        const authModule = useVuexStore<typeof authStore>('data/authentication');
        const user = authModule.getter('user');

        const modalModule = useVuexStore<typeof modalStore>('ui/modal');
        const setModal = modalModule.action('setModal');

        const isBetlerWelcomeOffer = computed(() => props.bonus.type === PromotionBonusType.WelcomeOffer
            && getConfig().countrySpecific.user.bonus.bonusProvider === BonusProvider.Betler);
        const isRegularPromotion = computed(() => props.bonus.type !== PromotionBonusType.WelcomeOffer);
        const actionButtonText = ref(isRegularPromotion.value ? translate('promotion.available.button_opt_in') : translate('promotion.available.button_choose_bonus'));
        const customConditionsPreview = useCustomConditionsPreview(props.conditions, props.kind);

        // stopping loading when opting process finishes
        watch(isPromotionOptInProcessing, (value) => {
            if (!value) {
                isLoading.value = value;
            }
        });

        watch(() => props.isExpanded, () => {
            if (props.isExpanded) {
                promotionActiveCardDiv.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
                areDetailsVisible.value = props.isExpanded;
            }
        });

        onMounted(() => {
            if (props.isExpanded) {
                promotionActiveCardDiv.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }
        });

        async function promoOptIn() {
            let analyticsLabel = null;
            if (!isActionButtonClicked.value) {
                isActionButtonClicked.value = true;
                actionButtonText.value = translate('promotion.available.button_confirm_opt_in');
                analyticsLabel = PromotionHubClickEventLabels.promotionsOptInFirstClick;
            } else {
                isLoading.value = true;
                isActionButtonClicked.value = false;
                if (isRegularPromotion.value) {
                    await promotionOptIn(props.optInCode);
                } else {
                    setModal(getWelcomeOfferModalData(props.welcomeBonuses, false));
                }
                analyticsLabel = PromotionHubClickEventLabels.promotionsOptIn;
                isLoading.value = false;
                actionButtonText.value = translate('promotion.available.button_opt_in');
            }
            AnalyticsSDK.getInstance().log(
                'Promo_Hub',
                {
                    eventAction: 'Click',
                    eventLabel: analyticsLabel,
                    promoId: props.promotionId.toString(),
                },
            );
        }
        function toggleDetails() {
            AnalyticsSDK.getInstance().log(
                'Promo_Hub',
                {
                    eventAction: 'Click',
                    eventLabel: areDetailsVisible.value
                        ? PromotionHubClickEventLabels.promotionsDetailsExpand
                        : PromotionHubClickEventLabels.promotionsDetailsCollapse,
                    promoId: props.promotionId.toString(),
                },
            );
        }

        return {
            promotionActiveCardDiv,
            areDetailsVisible,
            isLoading,
            isPromotionOptInProcessing,
            isRegularPromotion,
            isBetlerWelcomeOffer,
            isCTAButtonVisible: computed(() => !!props.optInCode && !!user.value),
            actionButtonText,
            minExpirationDate: props.expirationDate ? minDate([props.expirationDate, props.bonus.expirationDate ?? new Date(8.64e15)]) : undefined,
            bonusSummary: computed(() => props.kind === BonusKind.Betler && props.bonus.type !== PromotionBonusType.WelcomeOffer
                ? getBetlerBonusSummary(props.bonus.type, props.bonus.bonusAmount, props.bonus.rewardType) : translate(props.bonusSummaryTitle)),
            promoOptIn,
            toggleDetails,
            filteredWelcomeBonuses: props.welcomeBonuses.filter((bonus) => bonus.type !== WelcomeOfferBonusType.NoBonus) as BetlerWelcomeBonus[],
            isCustomConditionPreviewVisible: computed(() => !areDetailsVisible.value && props.kind === BonusKind.Comtrade),
            isConditionSectionVisible: computed(() => customConditionsPreview.length > 0 && props.kind === BonusKind.Comtrade && !areDetailsVisible.value),
        };
    },
    components: {
        CustomConditionsPreview,
        PromotionDetails,
        CardActionSection,
        PromotionWelcomeOfferDetails,
    },
});
